<template>
	<div class="feedback-widget" v-if="!isPublic">
		<!-- Floating Icon Button -->
		<button
			class="feedback-widget-icon"
			:class="{ 'is-open': showMenu }"
			@click="toggleMenu"
			type="button"
			v-if="!showForm"
			aria-label="Feedback">
			<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
				<path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" />
				<path d="M7 9h10v2H7zm0-3h10v2H7z" />
			</svg>
		</button>

		<!-- Options Menu -->
		<transition name="fade-slide">
			<div class="feedback-widget-menu" v-if="showMenu && !showForm">
				<div class="feedback-widget-menu-header">
					<h3>How can we help?</h3>
					<button @click="closeWidget" class="close-btn" type="button" aria-label="Close" />
				</div>
				<div class="feedback-widget-menu-options">
					<button @click="selectOption('contact')" class="option-btn" type="button">
						<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
							<path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
						</svg>
						<span>Contact Us</span>
					</button>
					<button @click="selectOption('feedback')" class="option-btn" type="button">
						<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
							<path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-7 12h-2v-2h2v2zm0-4h-2V6h2v4z" />
						</svg>
						<span>Leave Feedback</span>
					</button>
				</div>
			</div>
		</transition>

		<!-- Contact/Feedback Form -->
		<transition name="fade-slide">
			<div class="feedback-widget-form" v-if="showForm">
				<div class="feedback-widget-form-header">
					<h3>{{ formType === "contact" ? "Contact Us" : "Leave Feedback" }}</h3>
					<button @click="closeWidget" class="close-btn" type="button" aria-label="Close" />
				</div>
				<div class="feedback-widget-form-body">
					<p class="form-intro">
						{{
							formType === "contact"
								? "Got a question or need help? We're here for you!"
								: "We'd love to hear your thoughts and suggestions! (even the negative!)"
						}}
					</p>
					<FormInput
						type="textarea"
						v-model="comments"
						name="feedback-widget-comments"
						theRef="feedback-widget-comments"
						:placeholder="formType === 'contact' ? 'Tell us how we can help...' : 'Share your feedback with us...'"
						:focus="true"
						:required="false"
						:noMarginBottom="true"
						:minHeight="132"
						classes="feedback-textarea"
						:disabled="isSubmitting" />
					<div class="form-actions">
						<button @click="closeWidget" class="button hollow no-margin compact" type="button" :disabled="isSubmitting">Cancel</button>
						<button @click="submitForm" class="button brown no-margin compact" type="button" :disabled="isSubmitting || !comments.trim()">
							{{ isSubmitting ? "Sending..." : "Submit" }}
						</button>
					</div>
				</div>
			</div>
		</transition>
	</div>
</template>

<script>
import { ref, computed, watch } from "vue";
import { useRoute } from "vue-router";
import { useVueInstance, useNotifications } from "../composables/useVueInstance";
import ApiService from "../services/api.service";
import FormInput from "./Form/Input.vue";
import general from "../utils/general";

export default {
	name: "FeedbackWidget",
	components: {
		FormInput,
	},
	setup() {
		const { store } = useVueInstance();
		const { success, error } = useNotifications();
		const route = useRoute();

		const showMenu = ref(false);
		const showForm = ref(false);
		const formType = ref(null); // 'contact' or 'feedback'
		const comments = ref("");
		const isSubmitting = ref(false);
		const posthogRef = ref("");

		const brand = computed(() => store.state.general.brand);
		const parsedBrandId = computed(() => {
			const value = parseInt(brand.value, 10);
			return Number.isFinite(value) && value > 0 ? value : null;
		});
		const isPublic = computed(() => route.matched.some((record) => record.meta.public));
		const metadata = computed(() => store.state.feedback.widgetMetadata);
		const openRequest = computed(() => store.state.feedback.widgetOpenRequest);

		const ensureValidBrand = () => {
			if (parsedBrandId.value) return true;
			error("We couldn't determine your brand. Please refresh and try again.", "Oops");
			return false;
		};

		const toggleMenu = () => {
			if (!showMenu.value && !ensureValidBrand()) {
				return;
			}

			if (!showMenu.value) {
				// Capture PostHog event when opening widget
				capturePostHogEvent();
			}
			showMenu.value = !showMenu.value;
		};

		const clearOpenRequest = () => {
			store.commit("feedback/clearWidgetOpenRequest");
		};

		const capturePostHogEvent = () => {
			const timestamp = Math.floor(Date.now() / 1000);
			const ref = `${parsedBrandId.value}-${timestamp}`;
			posthogRef.value = ref;

			// Capture event with reference
			general.posthogCapture("issue-stamp", { info: `ref:${ref}` });
		};

		const openWidgetFromRequest = (payload = {}) => {
			if (!ensureValidBrand()) {
				clearOpenRequest();
				return;
			}

			if (!showMenu.value && !showForm.value) {
				capturePostHogEvent();
			}

			const nextType = payload?.type === "contact" || payload?.type === "feedback" ? payload.type : null;
			const nextMessage = typeof payload?.message === "string" ? payload.message : "";

			comments.value = nextMessage;

			if (nextType) {
				formType.value = nextType;
				showMenu.value = false;
				showForm.value = true;
			} else {
				formType.value = null;
				showForm.value = false;
				showMenu.value = true;
			}

			clearOpenRequest();
		};

		const selectOption = (type) => {
			if (!ensureValidBrand()) {
				return;
			}

			formType.value = type;
			showMenu.value = false;
			showForm.value = true;
		};

		const closeWidget = () => {
			showMenu.value = false;
			showForm.value = false;
			formType.value = null;
			comments.value = "";
			isSubmitting.value = false;
		};

		const collectMetadata = () => {
			const meta = { ...metadata.value };

			// Add current route info
			meta.currentUrl = window.location.href;
			meta.routeName = route.name;
			meta.routePath = route.path;

			return meta;
		};

		const submitForm = async () => {
			if (!ensureValidBrand()) {
				return;
			}

			if (!comments.value.trim()) {
				error("Please enter your message.", "Oops");
				return;
			}

			isSubmitting.value = true;

			try {
				const payload = {
					type: formType.value,
					posthogRef: posthogRef.value,
					comments: comments.value.trim(),
					metadata: collectMetadata(),
				};

				const response = await ApiService.post(`feedback/submit/${parsedBrandId.value}`, payload);

				if (response?.data?.message) {
					success(response.data.message, formType.value === "contact" ? "Message Sent" : "Feedback Received");
				} else {
					success(`Thank you! We've received your ${formType.value === "contact" ? "message" : "feedback"}.`, "Success");
				}

				closeWidget();
			} catch (err) {
				console.error("Feedback submission error:", err);
				const errorMessage =
					err?.response?.data?.message ||
					`Unable to submit your ${formType.value === "contact" ? "message" : "feedback"} right now. Please try again.`;
				error(errorMessage, "Oops");
			} finally {
				isSubmitting.value = false;
			}
		};

		watch(
			openRequest,
			(nextRequest) => {
				if (!nextRequest) return;
				openWidgetFromRequest(nextRequest);
			},
			{ deep: true, immediate: true }
		);

		return {
			showMenu,
			showForm,
			formType,
			comments,
			isSubmitting,
			posthogRef,
			isPublic,
			toggleMenu,
			selectOption,
			closeWidget,
			submitForm,
		};
	},
};
</script>

<style lang="less" scoped>
@import "./../assets/css/less/_settings.less";

.feedback-widget {
	position: fixed;
	bottom: 20px;
	right: 20px;
	z-index: 10000;
}

.feedback-widget-icon {
	width: 56px;
	height: 56px;
	border-radius: @radiusCircle;
	background-color: @guidedRose;
	color: white;
	border: none;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: @elevationButtonHoverDarkShadow;
	transition: all 0.3s ease;

	&:hover {
		background-color: darken(@guidedRose, 10%);
		transform: scale(1.05);
		box-shadow: @elevationButtonHoverStrongShadow;
	}

	&.is-open {
		background-color: darken(@guidedRose, 5%);
	}

	svg {
		width: 70%;
		height: 70%;
	}
}

.feedback-widget-menu,
.feedback-widget-form {
	position: absolute;
	bottom: 70px;
	right: 0;
	width: 320px;
	background: @surfacePanel;
	border-radius: @radiusMd;
	.elevation.modalWorkspace();
	overflow: hidden;
}

.feedback-widget-menu-header,
.feedback-widget-form-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	padding: 0 12px;
	background: @surfacePanel;
	border-bottom: 1px solid @borderRoseSubtle;

	h3 {
		margin: 0;
		font-size: 20px;
		font-weight: @bold;
		line-height: 1.2;
		color: @textGrey;
		padding: 20px 0 18px 12px;
		letter-spacing: 0.005em;
		flex: 1;
	}

	.close-btn {
		display: inline-block;
		width: 3em;
		height: 3em;
		border-radius: @radiusCircle;
		border: none;
		background: white url("../assets/img/close.svg") center center no-repeat;
		background-size: 45% auto;
		cursor: pointer;
		transition:
			background-color 0.2s ease,
			transform 0.5s ease-in-out;
		flex: 0 0 auto;

		&:hover,
		&:focus-visible {
			background-color: @controlHoverDarkTint;
			transform: rotate(180deg);
		}

		&:focus {
			outline: none;
		}

		&:focus-visible {
			box-shadow: 0 0 0 2px fade(@blue, 20%);
		}
	}
}

.feedback-widget-menu-options {
	padding: 14px 16px 16px;

	.option-btn {
		width: 100%;
		padding: 14px 16px;
		margin-bottom: 10px;
		background: @surfacePanel;
		border: 1px solid fade(@borderRoseBase, 80%);
		border-radius: @radiusXl;
		cursor: pointer;
		display: flex;
		align-items: center;
		gap: 12px;
		font-size: 15px;
		font-weight: @bold;
		color: @darkContrast;
		transition:
			border-color 0.2s ease,
			box-shadow 0.2s ease,
			transform 0.2s ease;

		&:last-child {
			margin-bottom: 0;
		}

		svg {
			color: @guidedRose;
			flex-shrink: 0;
		}

		&:hover {
			border-color: @guidedRose;
			transform: translateY(-1px);
			box-shadow: 0 8px 12px fade(@darkContrast, 10%);
		}
	}
}

.feedback-widget-form-body {
	padding: 18px 24px 24px;

	.form-intro {
		margin: 0 0 14px;
		font-size: 14px;
		line-height: 1.5;
	}

	.reference-info {
		margin: 0 0 16px;
		font-size: 13px;
		color: @textSubtle;
		background-color: #f5f5f5;
		padding: 8px 12px;
		border-radius: @radiusSm;

		strong {
			color: @guidedRose;
			font-weight: @bold;
		}
	}

	:deep(.feedback-textarea) {
		width: 100%;
		padding: 12px;
		border: 1px solid fade(@borderRoseBase, 80%);
		border-radius: @radiusMd;
		font-size: 14px;
		font-family: inherit;
		resize: vertical;
		transition:
			border-color 0.2s ease,
			box-shadow 0.2s ease;
		box-sizing: border-box;

		&:focus {
			outline: none;
			border-color: @guidedRose;
			box-shadow: 0 0 0 3px fade(@guidedRose, 10%);
		}

		&:disabled {
			background-color: #f5f5f5;
			cursor: not-allowed;
		}
	}

	.form-actions {
		display: flex;
		gap: 10px;
		margin-top: 18px;

		.button {
			flex: 1;

			&:disabled {
				opacity: 0.6;
				cursor: not-allowed;
			}
		}
	}
}

// Transitions
.fade-slide-enter-active,
.fade-slide-leave-active {
	transition: all 0.3s ease;
}

.fade-slide-enter-from {
	opacity: 0;
	transform: translateY(10px);
}

.fade-slide-leave-to {
	opacity: 0;
	transform: translateY(10px);
}

@media screen and (max-width: 767px) {
	.feedback-widget-menu,
	.feedback-widget-form {
		width: calc(100vw - 40px);
		max-width: 320px;
	}
	.feedback-widget {
		bottom: 6px;
		right: 6px;
	}
	.feedback-widget-icon {
		width: 40px;
		height: 40px;
	}
	.main-title {
		font-size: 1.75rem;
	}
}
</style>
